import { Product, ProductType, ProductImageType } from '../context/CartContext';

/**
 * Utility functions for product management and filtering
 */

/**
 * Filter products by product type
 */
export const filterProductsByType = (products: Product[], productType: ProductType): Product[] => {
  return products.filter(product => product.product_type === productType);
};

/**
 * Get all unique categories from a list of products
 */
export const getUniqueCategories = (products: Product[]): string[] => {
  const categories = products.map(product => product.category).filter(Boolean) as string[];
  return Array.from(new Set(categories));
};

/**
 * Get all unique sub-categories from a list of products
 */
export const getUniqueSubCategories = (products: Product[]): string[] => {
  const subCategories = products.map(product => product.sub_category).filter(Boolean) as string[];
  return Array.from(new Set(subCategories));
};

/**
 * Filter products by category
 */
export const filterProductsByCategory = (products: Product[], category: string): Product[] => {
  return products.filter(product => product.category === category);
};

/**
 * Filter products by sub-category
 */
export const filterProductsBySubCategory = (products: Product[], subCategory: string): Product[] => {
  return products.filter(product => product.sub_category === subCategory);
};

/**
 * Get the main product image (DETAIL type or first image)
 */
export const getMainProductImage = (product: Product): string => {
  const detailImage = product.images.find(img => img.image_type === ProductImageType.DETAIL);
  return detailImage?.image_path || product.images[0]?.image_path || '';
};

/**
 * Get the model/product display image (LARGE type or first image)
 */
export const getModelProductImage = (product: Product): string => {
  const largeImage = product.images.find(img => img.image_type === ProductImageType.LARGE);
  return largeImage?.image_path || product.images[0]?.image_path || '';
};

/**
 * Get the list/thumbnail image (LIST type or first image)
 */
export const getListProductImage = (product: Product): string => {
  const listImage = product.images.find(img => img.image_type === ProductImageType.LIST);
  return listImage?.image_path || product.images[0]?.image_path || '';
};

/**
 * Get product type display name
 */
export const getProductTypeDisplayName = (productType: ProductType): string => {
  switch (productType) {
    case ProductType.SNAPCHARM:
      return 'Custom Creature Charms';
    case ProductType.SPORTS:
      return 'Sports';
    case ProductType.LOCALS:
      return 'Locally Themed';
    default:
      return 'Unknown';
  }
};

/**
 * Get product type description
 */
export const getProductTypeDescription = (productType: ProductType): string => {
  switch (productType) {
    case ProductType.SNAPCHARM:
      return 'Adorable snap-on charms that can be worn as pendants or attached to various accessories';
    case ProductType.SPORTS:
      return 'Customizable sports merchandise including jerseys and equipment';
    case ProductType.LOCALS:
      return 'Personalized street signs and city limit signs';
    case ProductType.CUSTOM:
      return 'Custom-made products tailored to your specific needs';
    default:
      return 'A unique product from our collection';
  }
};

/**
 * Check if a product is customizable
 */
export const isProductCustomizable = (product: Product): boolean => {
  return Boolean(
    product.is_customizeable === true && 
    product.customization_items && 
    product.customization_items.length > 0
  );
};

/**
 * Get all products grouped by type
 */
export const groupProductsByType = (products: Product[]): Record<ProductType, Product[]> => {
  const grouped: Record<string, Product[]> = {};
  
  Object.values(ProductType).forEach(type => {
    grouped[type] = filterProductsByType(products, type);
  });
  
  return grouped as Record<ProductType, Product[]>;
};
